import { useState, useEffect, useCallback } from 'react'
import { Box, Typography, Paper, TextField, Button, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { LightMode, DarkMode, Water } from '@mui/icons-material'
import { useApi, apiPut } from '../../api/client'
import { useAuthStore } from '../../stores/auth.store'
import { useToastStore } from '../../stores/toast.store'

interface TenantData {
  id: string
  name: string
  slug: string
  theme: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: <LightMode /> },
  { value: 'dark', label: 'Dark', icon: <DarkMode /> },
  { value: 'blue', label: 'Classic Blue', icon: <Water /> },
]

export function SystemSettings() {
  const { data, mutate } = useApi<{ data: TenantData }>('/tenants/current')
  const tenant = data?.data
  const updateUser = useAuthStore((s) => s.updateUser)
  const user = useAuthStore((s) => s.user)
  const toast = useToastStore()

  const [name, setName] = useState('')
  const [theme, setTheme] = useState('light')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (tenant) {
      setName(tenant.name)
      setTheme(tenant.theme || 'light')
    }
  }, [tenant])

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast.warning('租户名称不能为空')
      return
    }
    setSaving(true)
    try {
      await apiPut('/tenants/current', { name: name.trim(), theme })
      mutate()
      if (user) {
        updateUser({ ...user, tenant_name: name.trim(), tenant_theme: theme })
      }
      toast.success('设置已保存')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [name, theme, mutate, updateUser, user, toast])

  if (!tenant) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 600 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>General</Typography>
        <TextField
          label="Tenant Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Slug"
          value={tenant.slug}
          fullWidth
          size="small"
          sx={{ mb: 3 }}
          disabled
        />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>UI Theme</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a theme for all users in this tenant.
        </Typography>
        <ToggleButtonGroup
          value={theme}
          exclusive
          onChange={(_, val) => val && setTheme(val)}
          size="small"
          sx={{ mb: 3 }}
        >
          {THEME_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {opt.icon}
                {opt.label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}
